import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { Product } from '@Types/product/Product';
import { ProductGrid, ProductItem } from 'components/tailgrids-ui/products/product-list';
import { SearchInput } from 'components/tailgrids-ui/products/product-list/SearchInput';
import { useFormat } from 'helpers/hooks/useFormat';
import { queryProducts } from 'frontastic/actions/products';
import { TYPESENSE_LABEL_PREFIX } from '../utils';

const productToComponentProduct = (
  product: Product,
  isTablet: boolean,
  isMobile: boolean,
  locale: string,
): ProductItem => {
  return {
    isTablet: isTablet,
    isMobile: isMobile,
    disabled: false,
    locale: locale,
    labelPrefix: TYPESENSE_LABEL_PREFIX,
    paddingX: '1',
    paddingY: '1',
    id: product.productId,
    variants: product.variants.map((variant) => {
      return {
        attributes: {
          color: variant.attributes?.['color'],
          inStock: variant.isOnStock ? 'true' : 'false',
          size: variant.attributes?.['commonSize'],
        },
        images: variant.images,
        description: product.description,
        href: product._url,
        sku: variant.sku,
        priceCentAmount: variant.price?.centAmount,
        discountedCentAmount: variant.discountedPrice?.centAmount,
        title: product.name,
        tags: [],
      };
    }),
  };
};

export const InstantSearchFallback = (props: { category?: string }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [value, setValue] = useState<string>('');
  const { formatMessage } = useFormat({ name: 'product' });
  const router = useRouter();
  const locale = router.locale;

  useEffect(() => {
    let cleanup = false;
    setLoading(true);
    queryProducts(value, props?.category).then((res) => {
      if (!cleanup) {
        setProducts(res?.['items'] as Product[]);
        setLoading(false);
      }
    });
    return () => {
      cleanup = true;
    };
  }, [props?.category, value]);

  return (
    <div>
      <div className="my-2 flex w-full flex-row justify-center rounded-sm bg-yellow-500 p-2 text-center text-black shadow-sm shadow-black">
        {formatMessage({ id: TYPESENSE_LABEL_PREFIX + 'fallback' })}
      </div>
      {!props?.category && (
        <SearchInput
          className="mb-2"
          placeholder={formatMessage({
            id: TYPESENSE_LABEL_PREFIX + 'inputPlaceholder',
            defaultMessage: 'Search for products...',
          })}
          defaultValue={value}
          onKeyPress={(e) => e.key.toLowerCase() === 'enter' && setValue(e.currentTarget.value)}
          //onChange={(e) => setValue(e.target.value)}
          data-testid="searchInput"
        />
      )}
      <div className="flex flex-row items-center justify-center">
        <div role="status" className={loading ? '' : 'hidden'}>
          <svg
            aria-hidden="true"
            className="mr-2 h-8 w-8 animate-spin fill-blue-600 text-gray-200 dark:text-gray-600"
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"
            />
          </svg>
          <span className="sr-only">Loading...</span>
        </div>
        <div className={!loading && products?.length === 0 ? '' : 'hidden'}>
          {formatMessage({ id: 'noProductsFound', defaultMessage: 'No products found.' })}
        </div>
      </div>
      {products?.length > 0 && (
        <ProductGrid
          fallback={true}
          loading={loading}
          products={products.map((product) => productToComponentProduct(product, true, true, locale))}
        />
      )}
    </div>
  );
};
